import { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import DataTable from "react-data-table-component";
import "flatpickr/dist/themes/material_blue.css";
import { createColumns } from "../../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../../constants/datatableStyles";
import CustomPagination from "../../../../Components/CustomPagination";
import { getWalletsReport, getWalletTypes } from "../../../../apis";
import PageTitle from "../../../../Components/UI/PageTitle";
import DropdownMultiSelect from "../../../../Components/UI/DropdownMultiSelect";
import RowLoader from "../../../../Components/UI/RowLoader";
import { transformKeys } from "../../../../_helper";
import ErrorAnimationComponent from "../../../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import RangeFilter from "../../../../Components/RangeFilter/RangeFilter";

const WalletsReport = () => {
  const [filterType, setFilterType] = useState("greater");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [filterToSend, setFilterToSend] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // Extract URL search parameters
  const searchParams = new URLSearchParams(location.search);
  const initialSearchTerm = searchParams.get("search") || "";
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;

  // State variables
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [currentPage, setCurrentPage] = useState(initialPage);

  const [requestsData, setRequestsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setExportLoading] = useState(false);
  const [isError, setIsError] = useState("");
  const [pagination, setPagination] = useState({});
  const [walletTypes, setWalletTypes] = useState([]);
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );
  const [selectedWalletTypes, setSelectedWalletTypes] = useState([]);

  const handleSelectedWalletTypes = selectedWalletTypes?.map(
    (type) => type.value
  );

  const fetchWalletsReport = () => {
    setIsLoading(true);
    getWalletsReport(
      searchTerm,
      handleSelectedWalletTypes,
      currentPage,
      filterToSend
    )
      .then((response) => {
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
        });
        const handleData = response.data.data.map((d) => transformKeys(d));
        setRequestsData(handleData);
      })
      .catch((error) => {
        setIsError(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  useEffect(() => {
    fetchWalletsReport();
  }, [searchTerm, selectedWalletTypes, currentPage, filterToSend]);

  // get dropdown data
  useEffect(() => {
    getWalletTypes()
      .then((response) => {
        setWalletTypes(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // Update URL parameters when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() }, { replace: true });
  }, [searchTerm, currentPage, navigate, sortDirection, sortKey]);

  // Handlers
  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { data } = await axios.get(
        `/admin/reports/clients-wallet/export-excel`,
        {
          params: {
            curPage: currentPage,
            search: searchTerm,
            sort_by: sortKey,
            sort_dir: sortDirection,
            WalletType: handleSelectedWalletTypes,
          },
          responseType: "blob",
        }
      );

      const fileName = `Wallets_Report_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const applyFilter = () => {
    if (filterType === "greater") {
      setFilterToSend({ balanceOperator: ">=", balanceValue: minValue });
    }
    if (filterType === "less") {
      setFilterToSend({ balanceOperator: "<=", balanceValue: maxValue });
    }
    if (filterType === "between") {
      setFilterToSend({ balanceFrom: minValue, balanceTo: maxValue });
    }
  };

  const resetFilter = () => {
    setFilterToSend({});
    setMinValue("");
    setMaxValue("");
  };

  const hiddenColumns = ["id", "client_id"]; // Add column keys you want to hide

  const filterAndSortData = () => {
    return requestsData;
  };

  const handleSortChange = (column, direction) => {
    setSortDirection(direction);
    setSortKey(column.sortKey);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const columns = useMemo(() => {
    return createColumns(requestsData, [], hiddenColumns, true);
  }, [requestsData]).map((col) => ({
    ...col,
    sortable: false,
    width: col.name === "Num" ? "80px" : "1fr",

    cell:
      col.name === "Amount"
        ? (row) => {
            const amount = parseFloat(row.amount.replace(/[^\d.-]/g, "")) || 0;
            const isNegative = amount < 0;

            return (
              <span
                className={isNegative ? "tw-text-red-500" : "tw-text-green-500"}
              >
                {row.amount} EGP
              </span>
            );
          }
        : col.cell,
  }));

  if (isError) return <ErrorAnimationComponent message={isError} />;

  return (
    <>
      <PageTitle title="Wallets Report" />

      <div className="bg-white flex flex-col">
        <div className="bg-white shadow-lg rounded-lg p-3 gap-2 mb-5">
          <div className="tw-flex tw-gap-2">
            <input
              type="text"
              onChange={handleFilterChange}
              value={searchTerm}
              className="border border-blue-300 rounded-lg p-2 tw-w-full"
              placeholder="Search by name, email, phone, referral code"
            />
            <div className="tw-w-1/3 max-xl:tw-w-full">
              <DropdownMultiSelect
                options={walletTypes}
                placeholder="Wallet Type"
                valueKey="WalletTypeId"
                labelKey="WalletTypeName"
                setSelectedItems={setSelectedWalletTypes}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>

          <RangeFilter
            filterType={filterType}
            setFilterType={setFilterType}
            minValue={minValue}
            setMinValue={setMinValue}
            maxValue={maxValue}
            setMaxValue={setMaxValue}
            applyFilter={applyFilter}
            resetFilter={resetFilter}
          />

          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            progressComponent={<RowLoader />}
            onSort={handleSortChange}
            sortServer
          />

          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {requestsData?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default WalletsReport;
